import {
    ChartPieIcon,
    HeartHandshakeIcon,
    MapPinIcon,
    ChartColumnIcon,
    UserRoundIcon,
    PlugIcon,
    ArrowLeftRightIcon,
    ReplaceAllIcon,
    MapPinnedIcon
} from 'lucide-react'
import DeviceIcon from './components/common/device-icon'
import { REPORT_TYPES } from './hooks/useReportTypes'

export const SYSTEM_COMPONENTS = {
    customers: 'Customers',
    locations: 'Locations',
    devices: 'Devices',
    reports: 'Reports',
    user_management: 'User management'
}

export const SYSTEM_COMPONENTS_IDS = {
    reports: 'reports',
    user_management: 'user_management',
    devices: 'devices',
    locations: 'locations',
    customers: 'customers'
}

export const DEFAULT_PERMISSIONS = ['create', 'read', 'update', 'delete']

export const SIDEBAR_MENU = {
    dashboard: 'dashboard',
    customers: 'customers',
    locations: 'locations',
    devices: 'devices',
    reports: 'reports',
    user_management: 'user_management',
    integrations: 'integrations',
    swap_replacements: 'swap_replacements',
    hot_swap: 'hot_swap',
    hot_swap_admin: 'hot_swap_admin'
}

export const SIDEBAR_MENU_NAMES = locale => ({
    dashboard: locale.dashboard.dashboard,
    customers: locale.customers.customers,
    locations: locale.locations.locations,
    devices: locale.devices.devices,
    reports: locale.reports.reports,
    user_management: locale.userManagement.userManagement,
    integrations: locale.integrations.integrations,
    swap_replacements: 'Austauschersätze',
    hot_swap: locale.hotSwap.hotSwap,
    hot_swap_admin: locale.hotSwap.hotSwapAdmin
})

export const SIDEBAR_MENU_ICONS = {
    dashboard: ChartPieIcon,
    customers: HeartHandshakeIcon,
    locations: MapPinIcon,
    devices: DeviceIcon,
    reports: ChartColumnIcon,
    user_management: UserRoundIcon,
    integrations: PlugIcon,
    swap_replacements: ArrowLeftRightIcon,
    hot_swap: ReplaceAllIcon,
    hot_swap_admin: ReplaceAllIcon
}

export const COMPONENTS_ROUTES = {
    dashboard: '/dashboard',
    customers: '/customers',
    locations: '/locations',
    devices: '/devices',
    reports: '/reports',
    user_management: '/user-management/users',
    sharing: '/sharing',
    integrations: '/integrations',
    swap_replacements: '/swap-replacements',
    hot_swap: '/hot-swap',
    hot_swap_admin: '/hot-swap-admin'
}

export const LOCAL_DATA_KEY = 'traplinked-data'
export const LOCAL_LOCALE_KEY = 'locale'

export const LOCALE_CODES = {
    en: 'en',
    de: 'de',
    nl: 'nl',
    pt: 'pt',
    es: 'es',
    fr: 'fr',
    cs: 'cs'
}

export const LOCALE_MAP = {
    en_US: 'en',
    de_DE: 'de',
    nl_NL: 'nl',
    pt_PT: 'pt',
    es_ES: 'es',
    fr_FR: 'fr',
    cs_CZ: 'cs'
}

// toLocaleString() uses this input format
export const LOCALE_MAP_ADJUSTED = {
    en: 'en-US',
    de: 'de-DE',
    nl: 'nl-NL',
    pt: 'pt-PT',
    es: 'es-ES',
    fr: 'fr-FR',
    cs: 'cs-CZ'
}

export const LOCALE_MAP_REVERSED = {
    en: 'en_US',
    de: 'de_DE',
    nl: 'nl_NL',
    pt: 'pt_PT',
    es: 'es_ES',
    fr: 'fr_FR',
    cs: 'cs_CZ'
}

export const LOCALE_DISPLAY_OPTIONS = {
    [LOCALE_CODES.de]: 'DE',
    [LOCALE_CODES.en]: 'EN',
    [LOCALE_CODES.nl]: 'NL',
    [LOCALE_CODES.pt]: 'PT',
    [LOCALE_CODES.es]: 'ES',
    [LOCALE_CODES.fr]: 'FR',
    [LOCALE_CODES.cs]: 'CS'
}

export const TERMS_LOCALE_DISPLAY_OPTIONS = {
    [LOCALE_CODES.de]: 'DE',
    [LOCALE_CODES.en]: 'EN'
}

export const DEVICES_BATTERY_STATUS_MAX = 6000
export const DEVICES_BATTERY_STATUS_MIN = 0

export const getDeviceSleepTimeFrames = locale => ({
    14400: locale.common.fourHours,
    21600: locale.common.sixHours,
    43200: locale.common.twelveHours,
    64800: locale.common.eighteenHours,
    86400: locale.common.twentyFourHours
})

export const DEFAULT_ROLES = {
    traplinked_admin: 'traplinked_admin',
    customer_admin: 'customer_admin',
    viewer: 'viewer',
    management_user: 'management_user',
    read_only_user: 'read_only_user',
    location_management_user: 'location_management_user',
    technician: 'technician'
}

export const USER_RELATIONS = {
    internal: 'internal',
    external: 'external'
}

export const RELATIONS = {
    externalIn: 'external_in',
    externalOut: 'external_out',
    internal: 'internal',
    nonexistent: 'nonexistent'
}

export const CUSTOM_ROLE = 'custom_role'

export const SOFTWARE_PLANS = {
    free: 'free',
    premium: 'premium',
    pro: 'pro',
    traplinked_admin: 'traplinked_admin'
}

export const FREE_PLAN_RESTRICTED_PATHNAMES = {
    integrations: '/integrations'
}

export const FIRMWARE_VERSION_WITH_WIFI_RSSI = '2.1.0'
export const TOM_FIRMWARE_VERSION_WITH_WIFI_RSSI = '1.0.0'

export const FIRMWARE_VERSION_NO_UPDATE = 'noupdate'

export const OPERATION_MODES = {
    snaptrap: 'snaptrap',
    movement: 'movement',
    insect: 'insect',
    bait: 'bait'
}

export const DEVICE_TYPE = {
    tom: 'tom',
    jerry: 'jerry',
    'jerry-lora': 'jerry-lora',
    'tm01-1': 'tm01-1',
    trapsensor: 'trapsensor',
    'jerry-bait-rat': 'jerry-bait-rat'
}

export const OPERATION_MODES_PEST_CONTROL_SYSTEMS = {
    [OPERATION_MODES.snaptrap]: [1, 2, 3, 5, 6, 11],
    [OPERATION_MODES.movement]: [1, 2, 3, 5, 6, 7, 11],
    [OPERATION_MODES.insect]: [8, 9, 10],
    [OPERATION_MODES.bait]: [1]
}

export const WIFI_RSSI_LIMITS = {
    [DEVICE_TYPE['tom']]: {
        badSignal: { min: -999, max: -80 },
        lowSignal: { min: -80, max: -65 },
        goodSignal: { min: -65, max: -50 },
        excellentSignal: { min: -50, max: 0 }
    },
    [DEVICE_TYPE['jerry']]: {
        badSignal: { min: -999, max: -80 },
        lowSignal: { min: -80, max: -65 },
        goodSignal: { min: -65, max: -50 },
        excellentSignal: { min: -50, max: 0 }
    },
    [DEVICE_TYPE['jerry-lora']]: {
        badSignal: { min: -999, max: -95 },
        lowSignal: { min: -95, max: -75 },
        goodSignal: { min: -75, max: -40 },
        excellentSignal: { min: -40, max: 20 }
    },
    [DEVICE_TYPE['jerry-bait-rat']]: {
        badSignal: { min: -999, max: -95 },
        lowSignal: { min: -95, max: -75 },
        goodSignal: { min: -75, max: -40 },
        excellentSignal: { min: -40, max: 20 }
    },
    [DEVICE_TYPE['tm01-1']]: {
        badSignal: { min: -999, max: -80 },
        lowSignal: { min: -80, max: -65 },
        goodSignal: { min: -65, max: -50 },
        excellentSignal: { min: -50, max: 0 }
    },
    [DEVICE_TYPE.trapsensor]: {
        badSignal: { min: 0, max: 25 },
        lowSignal: { min: 25, max: 50 },
        goodSignal: { min: 50, max: 75 },
        excellentSignal: { min: 75, max: 100 }
    }
}

export const REPRESENTATION = {
    all: 'all',
    assigned: 'assigned'
}

export const LOGIN_EX_TYPES = {
    wrong_login_data: 'wrong_login_data'
}

export const DELIGHTED_TAG_ID = 'delighted-survey'

export const SETTINGS_DROPDOWN_URLS = {
    de: {
        scribe: 'https://www.traplinked.com/anleitung/',
        terms: 'https://www.traplinked.com/wp-content/uploads/2024/01/AGB_traplinkedGmbH_012024.pdf',
        privacy: 'https://www.traplinked.com/datenschutz/',
        roadmap: 'https://roadmap.traplinked.com/roadmap',
        featureIdeas: 'https://roadmap.traplinked.com/b/kvjj5yve/feature-ideas',
        chat: 'https://6934192.hs-sites.com/in-app-support-chat',
        bugReport: 'https://wbz3iygfon4.typeform.com/bug-report-de'
    },
    common: {
        scribe: 'https://www.traplinked.com/en/manual/',
        terms: 'https://www.traplinked.com/wp-content/uploads/2024/01/AGB_EN_traplinkedGmbH_012024.pdf',
        privacy: 'https://www.traplinked.com/en/data-privacy/',
        roadmap: 'https://roadmap.traplinked.com/roadmap',
        featureIdeas: 'https://roadmap.traplinked.com/b/kvjj5yve/feature-ideas',
        chat: 'https://6934192.hs-sites.com/in-app-support-chat',
        bugReport: 'https://wbz3iygfon4.typeform.com/bug-report-en'
    }
}

export const HOTSWAP_PERMISSIONS = {
    allowed_hotswap_request: 'allowed_hotswap_request',
    allowed_update_service: 'allowed_update_service'
}

export const CARRIER_PROVIDERS = ['DHL', 'UPS', 'GLS']

export const PCB_REVISIONS_BEFORE_CATCH_DETECTION = {
    GATEWAY: 'GATEWAY',
    TOM01: 'TOM01',
    'EU-J102 LoRa': 'EU-J102 LoRa',
    'Knock sensor test': 'Knock sensor test',
    'EU-JLRA': 'EU-JLRA',
    2.4: '2.4',
    LORAGW: 'LORAGW',
    LORA: 'LORA',
    '2.0': '2.0',
    'EU-J103 WiFi': 'EU-J103 WiFi',
    'EU-J103': 'EU-J103',
    2.5: '2.5',
    1.7: '1.7',
    'EU-J01J': 'EU-J01J',
    TOM02: 'TOM02',
    'EU-J102 WiFi': 'EU-J102 WiFi',
    '2.4.1': '2.4.1',
    1: '1',
    RKS: 'RKS',
    'EU-J103-LORA': 'EU-J103-LORA',
    '2.4.2': '2.4.2'
}

export const PCB_REVISION_WITH_ADHOC_CATCH_DETECTION = '2.4.3'

export const DASHBOARD_MAP_SWITCHER_OPTIONS = {
    dashboard: { value: 'dashboard', icon: ChartPieIcon },
    mapView: { value: 'mapView', icon: MapPinnedIcon }
}

export const DEFAULT_DASHBOARD_MAP_SWITCHER_OPTION =
    DASHBOARD_MAP_SWITCHER_OPTIONS.mapView.value

export const ENABLE_MOBILE_VIEW_FOR_SPECIFIC_PATHS = [
    '/reset-password',
    '/forgot-password',
    '/register'
]

export const TRANSFER_MODE = {
    wifi: '0',
    lora: '1',
    cellular: '2'
}

export const STAND_BY_FUSION_VALUE = 'TEST'
export const SIM_CARD_STATUS = {
    active: 'ACTIVE',
    deactive: 'DEACTIVATED',
    pending: 'PENDING',
    standBy: 'STANDBY'
}

export const FLOOR_PLAN_MAX_SIZE = 1.5 * 1024 * 1024

export const MOVE_TO_LOCATION_MODAL_STEPS = {
    not_assigned_step: 'not_assigned_step',
    assigned_step: 'assigned_step',
    location_select: 'location_select',
    confirmation: 'confirmation'
}

export const DELETE_DEVICES_MODAL_STEPS = {
    not_assigned_step: 'not_assigned_step',
    assigned_step: 'assigned_step',
    confirmation: 'confirmation'
}

export const ESCALATION_LEVELS_REPORT_TYPES = [
    REPORT_TYPES.triggered,
    REPORT_TYPES.down,
    REPORT_TYPES.false_triggering,
    REPORT_TYPES.catch
]

export const PCB_REVISION_WITH_PROPER_CATCH_DETECTION = '3.2.0'

export const FIRMWARE_VERSION_WITH_PROPER_CATCH_DETECTION = '4.0.0'

export const LOCATION_STATS_RANGES = {
    lastMonth: 'last_month',
    lastWeek: 'last_week'
}

export const DEFAULT_BAIT_ACTIVITY_ALERT_THRESHOLD = '10_1_d'
export const DEFAULT_BAIT_MANUFACTURER_AND_TYPE = ''
export const DEFAULT_BAIT_WEIGHT_GRAMS = 40
export const DEFAULT_BAIT_CALIBRATION_WEIGHT_GRAMS = 40

export const DEFAULT_BAIT_SETTINGS = {
    is_bait_monitoring_enabled: true,
    activity_alert_threshold: DEFAULT_BAIT_ACTIVITY_ALERT_THRESHOLD,
    bait_manufacturer_and_type: DEFAULT_BAIT_MANUFACTURER_AND_TYPE,
    bait_weight_grams: DEFAULT_BAIT_WEIGHT_GRAMS,
    bait_calibration_weight_grams: DEFAULT_BAIT_CALIBRATION_WEIGHT_GRAMS
}
